"use client";

import {
  Button,
  Flex,
  Heading,
  ProgressCircle,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useCallback, useEffect, useState } from "react";
import { LuArrowLeft, LuPackage } from "react-icons/lu";

import CargoListModal from "@features/services/shipping/CargoListModal";
import MeasuredTotalsCard from "@features/services/shipping/MeasuredTotalsCard";
import { toMeasuredTotals } from "@features/services/shipping/measuredTotals";
import type { MeasuredTotals } from "@features/services/shipping/measuredTotals";
import ShipmentTimeline from "@features/services/shipping/ShipmentTimeline";
import type { ReceivedPackageData } from "@features/services/shipping/ShipmentTimeline";
import { ShippingLabel } from "@features/services/shipping/ShippingLabel";
import type { CargoItem } from "@features/services/shipping/types";
import type { ContainerPricingUnit } from "helpers/pricingUnit";
import { lookupShipmentAction } from "server/actions/shipment";
import { useOrgId } from "hooks/useOrgId";

export interface ShipmentEvent {
  event: string;
  timestamp: Date;
}

interface TrackingViewProps {
  trackingRef: string;
  onBack: () => void;
}

const TrackingView = ({ trackingRef, onBack }: TrackingViewProps) => {
  const orgId = useOrgId();
  const [events, setEvents] = useState<ShipmentEvent[]>([]);
  const [receivedPackages, setReceivedPackages] = useState<
    ReceivedPackageData[]
  >([]);
  const [cargoItems, setCargoItems] = useState<CargoItem[]>([]);
  const [pricingUnit, setPricingUnit] = useState<ContainerPricingUnit>("CBM");
  const [measured, setMeasured] = useState<MeasuredTotals | null>(null);
  const [warehouseAddress, setWarehouseAddress] = useState<string | null>(null);
  const [senderName, setSenderName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cargoModalOpen, setCargoModalOpen] = useState(false);

  const onClickBack = useCallback(() => {
    onBack();
  }, [onBack]);

  const onClickViewCargo = useCallback(() => {
    setCargoModalOpen(true);
  }, []);

  const onCloseCargo = useCallback(() => {
    setCargoModalOpen(false);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    lookupShipmentAction(orgId, trackingRef)
      .then((result) => {
        if (cancelled) {
          return;
        }
        if (result == null) {
          setError("No shipment found for this tracking number.");
        } else {
          setEvents(result.events);
          setReceivedPackages(result.receivedPackages);
          setCargoItems(result.packingList ?? []);
          setPricingUnit(result.containerPricingUnit);
          setSenderName(result.senderName);
          setWarehouseAddress(result.warehouseAddress);
          setMeasured(toMeasuredTotals(result));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError("Something went wrong. Please try again.");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [trackingRef]);

  return (
    <Flex direction="column" align="center" gap={2} p="40px 20px">
      <Flex w="100%" maxW="500px" direction="column" gap={6}>
        <Button
          variant="ghost"
          size="sm"
          alignSelf="flex-start"
          px={0}
          _hover={{ bg: "transparent" }}
          onClick={onClickBack}
        >
          <LuArrowLeft />
          Back
        </Button>

        <Stack gap={1}>
          <Heading
            fontSize="lg"
            fontWeight={600}
            letterSpacing=".06em"
            color="text.primary"
          >
            Shipment Tracking
          </Heading>
          <Text fontSize="sm" color="text.secondary">
            {trackingRef}
          </Text>
        </Stack>

        {loading && (
          <Flex align="center" justify="center" gap={2} py={8}>
            <ProgressCircle.Root value={null} size="xs">
              <ProgressCircle.Circle>
                <ProgressCircle.Track />
                <ProgressCircle.Range />
              </ProgressCircle.Circle>
            </ProgressCircle.Root>
            <Text fontSize="sm" color="text.secondary">
              Loading...
            </Text>
          </Flex>
        )}

        {error != null && (
          <Text fontSize="sm" color="red.400">
            {error}
          </Text>
        )}

        {!loading && error == null && (
          <>
            <MeasuredTotalsCard
              measured={measured}
              packageCount={receivedPackages.length}
            />
            {cargoItems.length > 0 && (
              <Button
                variant="outline"
                size="xs"
                alignSelf="flex-end"
                borderColor="border.light"
                onClick={onClickViewCargo}
              >
                <LuPackage />
                View Cargo ({cargoItems.length})
              </Button>
            )}
            <ShippingLabel
              trackingRef={trackingRef}
              warehouseAddress={warehouseAddress}
              customerName={senderName}
            />
            <ShipmentTimeline
              events={events}
              receivedPackages={receivedPackages}
            />
          </>
        )}
      </Flex>

      <CargoListModal
        open={cargoModalOpen}
        onClose={onCloseCargo}
        pricingUnit={pricingUnit}
        items={cargoItems}
      />
    </Flex>
  );
};

export default TrackingView;
